"use client";

import { ChangeEvent, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ACTIVE_COUNTRIES,
  getSortedCountries,
} from "./active-countries-config";

// World map topology - using our own hosted TopoJSON file
// Created from Natural Earth 110m cultural vectors
// This file is hosted locally to ensure we have control over the data
// Crimea is correctly included as part of Ukraine (MultiPolygon with 2 polygons)
// Cache-busting query parameter ensures browsers load the latest version
const geoUrl = "/world-corrected.json?v=2";

interface WorldMapSectionProps {
  title: string;
  subtitle: string;
}

export default function WorldMapSection({
  title,
  subtitle,
}: WorldMapSectionProps) {
  const { i18n } = useTranslation();
  const currentLocale = (i18n.language || "en") as "en" | "de" | "uk";

  // Track which country is currently hovered (for bidirectional highlighting)
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  // Get sorted countries from config
  const countries = useMemo(() => getSortedCountries(), []);

  // Create a map of country codes to country data for fast lookup
  const countryMap = useMemo(() => {
    const map = new Map();
    ACTIVE_COUNTRIES.forEach((country) => {
      map.set(country.countryCode.toLowerCase(), country);
    });
    return map;
  }, []);

  // Get country names in current locale (defaulting to English)
  const getCountryName = (country: {
    countryName: Record<string, string>;
    countryCode: string;
  }) => {
    const names = country.countryName;
    return names[currentLocale] || names.en || country.countryCode;
  };

  const handleCountrySelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setHoveredCountry(value === "" ? null : value);
  };

  return (
    <section className="py-2 sm:py-4 lg:py-6 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-4 sm:mb-5">
          <div className="flex items-center justify-center mb-2">
            <Globe className="h-6 w-6 sm:h-7 sm:w-7 text-primary-600" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5">
            {title}
          </h2>
          <p className="text-sm sm:text-sm text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="relative">
          <ComposableMap
            className="w-full h-auto"
            // projectionConfig={{ scale: 180 }}
            width={850}
            height={450}
          >
            <Geographies geography={geoUrl}>
              {({ geographies, error }) => {
                if (error) {
                  console.error("Map loading error:", error);
                  return (
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      fill="#666"
                      fontSize="14"
                    >
                      Error loading map. Please refresh the page.
                    </text>
                  );
                }
                if (!geographies || geographies.length === 0) {
                  return (
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      fill="#666"
                      fontSize="14"
                    >
                      Loading map data...
                    </text>
                  );
                }
                return geographies.map((geo) => {
                  // TopoJSON uses ISO_A2 property
                  const isoA2 = geo.properties?.ISO_A2;
                  const countryCode =
                    typeof isoA2 === "string" ? isoA2.toLowerCase() : undefined;
                  // Ensure Ukraine (UA) is correctly recognized - Crimea is part of Ukraine
                  const normalizedCode =
                    countryCode === "ua" ? "ua" : countryCode;
                  const isActive = normalizedCode
                    ? countryMap.has(normalizedCode)
                    : false;
                  const isHovered = normalizedCode === hoveredCountry;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={
                        isHovered && isActive
                          ? "#e8e582"
                          : isActive
                          ? "#cdc50a"
                          : "#E5E7EB"
                      }
                      stroke="#FFFFFF"
                      strokeWidth={0.5}
                      {...({
                        onMouseEnter: () => {
                          if (isActive && normalizedCode) {
                            setHoveredCountry(normalizedCode);
                          }
                        },
                        onMouseLeave: () => {
                          setHoveredCountry(null);
                        },
                      } as unknown as Record<string, unknown>)}
                      style={{
                        default: {
                          outline: "none",
                        },
                        hover: {
                          fill: isActive ? "#e8e582" : "#F4F4F5",
                          outline: "none",
                          cursor: isActive ? "pointer" : "default",
                        },
                        pressed: {
                          outline: "none",
                        },
                      }}
                    />
                  );
                });
              }}
            </Geographies>
          </ComposableMap>

          {/* Active Countries List - Compact tag-style layout */}
          {countries && countries.length > 0 && (
            <div className="mt-5 sm:mt-6 w-full">
              <div className="sm:hidden">
                <label htmlFor="active-countries-mobile" className="sr-only">
                  Select a country
                </label>
                <select
                  id="active-countries-mobile"
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={hoveredCountry ?? ""}
                  onChange={handleCountrySelect}
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option
                      key={country.countryCode}
                      value={country.countryCode.toLowerCase()}
                    >
                      {getCountryName({
                        countryCode: country.countryCode,
                        countryName: country.countryName as Record<
                          string,
                          string
                        >,
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hidden sm:flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
                {countries.map((country) => {
                  const lowerCaseCode = country.countryCode.toLowerCase();
                  const isHovered = hoveredCountry === lowerCaseCode;
                  return (
                    <div
                      key={country.countryCode}
                      className={`px-2.5 py-1.5 rounded-full transition-colors border cursor-pointer ${
                        isHovered
                          ? "bg-yellow-100 border-yellow-400 shadow-sm"
                          : "bg-gray-50 hover:bg-gray-100 border-gray-200"
                      }`}
                      onMouseEnter={() => setHoveredCountry(lowerCaseCode)}
                      onMouseLeave={() => setHoveredCountry(null)}
                    >
                      <span className="text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">
                        {getCountryName({
                          countryCode: country.countryCode,
                          countryName: country.countryName as Record<
                            string,
                            string
                          >,
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
